import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, MemoryRouter, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { PortalNotice } from './components/PortalNotice';
import { ChallengePopup, PartyInvitePopup } from './components/ChallengePopup';
import { Icon } from './components/Icon';
import { ContentPage } from './content/routes';
import { Toasts } from './components/Toasts';
import { Spinner } from './components/ui';
import { setLocale } from './i18n';
import { music, moodFor } from './lib/music';
import { trackPage } from './lib/analytics';
import { initNative, prefersReducedMotion } from './lib/platform';
import { authApi } from './lib/api';
import { lobbySocket } from './lib/lobbySocket';
import { instantMultiplayer, invitedRoom, onPortalJoinRoom, onPortalSignIn, portalUserToken, PORTAL } from './lib/portal';
import { activeChatUserId } from './screens/Friends';
import { Home } from './screens/Home';
import { ResetPassword } from './screens/ResetPassword';
import { Welcome } from './screens/Welcome';
import { installLobbyHandlers, useLobby } from './store/lobby';
import { useSession } from './store/session';
import { useSettings } from './store/settings';
import { DISPLAY_NAME_MAX, seoPageFor, type Locale } from '@snakefeast/shared';

// Secondary screens are code-split so the first paint only ships the lobby.
const GameScreen = lazy(() => import('./screens/Game').then((m) => ({ default: m.GameScreen })));
const SoloScreen = lazy(() => import('./screens/Solo').then((m) => ({ default: m.SoloScreen })));
const RoomScreen = lazy(() => import('./screens/Room').then((m) => ({ default: m.RoomScreen })));
const JoinRoute = lazy(() => import('./screens/Room').then((m) => ({ default: m.JoinRoute })));
const Leaderboards = lazy(() => import('./screens/Leaderboards').then((m) => ({ default: m.Leaderboards })));
const Friends = lazy(() => import('./screens/Friends').then((m) => ({ default: m.Friends })));
const Profile = lazy(() => import('./screens/Profile').then((m) => ({ default: m.Profile })));
const PublicProfile = lazy(() => import('./screens/Profile').then((m) => ({ default: m.PublicProfile })));
const Settings = lazy(() => import('./screens/Settings').then((m) => ({ default: m.Settings })));
const Premium = lazy(() => import('./screens/Premium').then((m) => ({ default: m.Premium })));
const SeasonScreen = lazy(() => import('./screens/Season').then((m) => ({ default: m.SeasonScreen })));
const Shop = lazy(() => import('./screens/Shop').then((m) => ({ default: m.Shop })));
const Clans = lazy(() => import('./screens/Clans').then((m) => ({ default: m.Clans })));
const Tournaments = lazy(() => import('./screens/Tournaments').then((m) => ({ default: m.Tournaments })));
const TournamentDetail = lazy(() => import('./screens/Tournaments').then((m) => ({ default: m.TournamentDetailScreen })));
const Live = lazy(() => import('./screens/Live').then((m) => ({ default: m.Live })));
const MySnake = lazy(() => import('./screens/MySnake').then((m) => ({ default: m.MySnake })));

let handlersInstalled = false;
// Portals serve the build from an arbitrary path inside their iframe, so routing can't live in the URL there.
const Router = PORTAL ? MemoryRouter : BrowserRouter;

function Nav() {
  const { t } = useTranslation();
  const unread = useLobby((s) => Object.values(s.unread).reduce((a, b) => a + b, 0) + s.clanUnread);
  const items = [
    { to: '/', icon: 'play', label: t('nav.play') },
    { to: '/leaderboards', icon: 'trophy', label: t('nav.leaderboards') },
    { to: '/friends', icon: 'users', label: t('nav.friends'), badge: unread },
    { to: '/profile', icon: 'user', label: t('nav.profile') },
    { to: '/settings', icon: 'settings', label: t('nav.settings') },
  ];
  return (
    <nav className="nav" aria-label="main">
      {items.map((i) => (
        <NavLink key={i.to} to={i.to} end={i.to === '/'} className={({ isActive }) => (isActive ? 'active' : '')}>
          <Icon name={i.icon} />
          <span>{i.label}</span>
          {!!i.badge && <span className="badge">{i.badge}</span>}
        </NavLink>
      ))}
    </nav>
  );
}

function MatchRedirect() {
  const match = useLobby((s) => s.match);
  const nav = useNavigate();
  const loc = useLocation();
  useEffect(() => {
    if (match && loc.pathname !== '/game') nav('/game');
  }, [match, loc.pathname, nav]);
  return null;
}

/**
 * A guest playing on CrazyGames while signed in there becomes that CrazyGames account, so their
 * progress follows them to any device. If the account already belongs to a player (from another
 * device), this device switches to that player. True when the session is now the account's.
 */
async function linkPortalAccount(locale: string): Promise<boolean> {
  const me = useSession.getState().user;
  if (PORTAL !== 'crazygames' || !me?.isGuest) return false;
  const token = await portalUserToken();
  if (!token) return false;
  try {
    const r = await authApi.crazygames(token, locale);
    if (r.id !== me.id) lobbySocket.stop(); // another player now: reconnect as them
    useSession.getState().signedIn(r);
    return true;
  } catch {
    return false;
  }
}

let instantRoomCreated = false;

/** Portal invites (on launch and while playing) open the room; signing in to the portal links (or renames) the player. */
function PortalBridge() {
  const nav = useNavigate();
  const { i18n } = useTranslation();
  const updateMe = useSession((s) => s.updateMe);
  useEffect(() => {
    if (!PORTAL) return;
    const code = invitedRoom();
    if (code) nav(`/join/${code}`);
    // Opened by a party leader ("play with friends"): straight into a new private room to invite them to.
    else if (instantMultiplayer() && !instantRoomCreated) {
      instantRoomCreated = true; // once per launch (StrictMode runs effects twice in dev)
      lobbySocket.send({ t: 'room.create', mode: 'ffa', maxPlayers: 8, variant: 'endless' });
    }
    onPortalJoinRoom((c) => nav(`/join/${c}`));
    void linkPortalAccount(i18n.language);
    onPortalSignIn(async (name) => {
      if (await linkPortalAccount(i18n.language)) return;
      void updateMe({ displayName: name.slice(0, DISPLAY_NAME_MAX) }).catch(() => {});
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function DeepLinks() {
  const nav = useNavigate();
  useEffect(() => {
    void initNative((path) => nav(path));
  }, [nav]);
  return null;
}

function Authed() {
  const loc = useLocation();
  const inGame = loc.pathname === '/game' || loc.pathname === '/practice' || loc.pathname === '/solo';
  return (
    <>
      <MatchRedirect />
      <PortalBridge />
      <Suspense fallback={<div className="empty"><Spinner /></div>}>
        <Routes>
          <Route path="/game" element={<GameScreen />} />
          <Route path="/practice" element={<GameScreen practice />} />
          <Route path="/solo" element={<SoloScreen />} />
          <Route
            path="*"
            element={
              <div className="shell">
                <Nav />
                <main className="main">
                  <Suspense fallback={<div className="empty"><Spinner /></div>}>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/room" element={<RoomScreen />} />
                      <Route path="/join/:code" element={<JoinRoute />} />
                      <Route path="/leaderboards" element={<Leaderboards />} />
                      <Route path="/friends" element={<Friends />} />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/u/:id" element={<PublicProfile />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/premium" element={PORTAL ? <Navigate to="/" replace /> : <Premium />} />
                      <Route path="/season" element={<SeasonScreen />} />
                      <Route path="/shop" element={<Shop />} />
                      <Route path="/clans" element={<Clans />} />
                      <Route path="/tournaments" element={<Tournaments />} />
                      <Route path="/tournaments/:id" element={<TournamentDetail />} />
                      <Route path="/live" element={<Live />} />
                      <Route path="/snake" element={<MySnake />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </Suspense>
                </main>
              </div>
            }
          />
        </Routes>
      </Suspense>
      {!inGame && <ChallengePopup />}
      {!inGame && <PartyInvitePopup />}
    </>
  );
}

/**
 * Background music follows the route. Browsers only allow audio after a user
 * gesture, so it starts on the first tap or key press.
 */
/** Analytics page views. Runs after the page's own effects, so content pages have set their title. */
function PageViews() {
  const loc = useLocation();
  useEffect(() => {
    trackPage(loc.pathname + loc.search);
  }, [loc.pathname, loc.search]);
  return null;
}

function MusicDirector() {
  const loc = useLocation();
  const { sound, music: musicOn, musicVolume } = useSettings();
  const [unlocked, setUnlocked] = useState(false);
  useEffect(() => {
    if (unlocked) return;
    const go = () => setUnlocked(true);
    window.addEventListener('pointerdown', go, { once: true });
    window.addEventListener('keydown', go, { once: true });
    return () => {
      window.removeEventListener('pointerdown', go);
      window.removeEventListener('keydown', go);
    };
  }, [unlocked]);
  useEffect(() => {
    if (!unlocked) return;
    const sync = () => {
      if (document.hidden || !sound || !musicOn) music.stop();
      else music.play(moodFor(loc.pathname));
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, [unlocked, loc.pathname, sound, musicOn, musicVolume]);
  return null;
}

function Root() {
  const loc = useLocation();
  const status = useSession((s) => s.status);
  const user = useSession((s) => s.user);
  const reduceMotion = useSettings((s) => s.reduceMotion);
  const leftHanded = useSettings((s) => s.leftHanded);

  useEffect(() => {
    document.documentElement.classList.toggle('reduce-motion', reduceMotion || prefersReducedMotion());
  }, [reduceMotion]);

  useEffect(() => {
    document.documentElement.classList.toggle('left-handed', leftHanded);
  }, [leftHanded]);

  useEffect(() => {
    if (user?.locale && user.locale !== localStorage.getItem('snakefeast.locale')) void setLocale(user.locale as Locale);
  }, [user?.locale]);

  // Public content pages look the same signed in or out, and must not wait for the session.
  const content = seoPageFor(loc.pathname);
  if (content) return <ContentPage path={content.path} />;
  // A "forgot password" email's link: works the same signed in or out.
  if (loc.pathname === '/reset-password') return <ResetPassword />;

  if (status === 'loading') {
    return (
      <div className="welcome">
        <img src={`${import.meta.env.BASE_URL}icon.svg`} alt="" className="logo-mark" />
      </div>
    );
  }
  return (
    <>
      <DeepLinks />
      {status === 'anon' ? (
        <Routes>
          <Route path="*" element={<Welcome />} />
        </Routes>
      ) : (
        <Authed />
      )}
    </>
  );
}

export function App() {
  useEffect(() => {
    if (!handlersInstalled) {
      handlersInstalled = true;
      installLobbyHandlers(() => useSession.getState().user?.id, () => activeChatUserId);
    }
    void useSession.getState().bootstrap();
  }, []);
  return (
    <Router>
      <div className="ambient" aria-hidden="true">
        <div className="blob b1" />
        <div className="blob b2" />
        <div className="blob b3" />
      </div>
      <Root />
      <MusicDirector />
      <PageViews />
      <Toasts />
      <PortalNotice />
    </Router>
  );
}
